/**
 * Type definitions for Cache Settings
 * These types match the backend API structure for cache configuration
 */

/**
 * Resource types that can have individual TTL settings
 */
export type ResourceType =
  | 'teams'
  | 'sites'
  | 'recentDocuments'
  | 'siteDocuments'
  | 'folderChildren'
  | 'authMethods'
  | 'registrationCampaign'
  | 'conditionalAccessPolicies'
  | 'transportRules';

/**
 * TTL (Time-To-Live) settings in seconds for each resource type
 */
export interface TtlSeconds {
  teams: number;
  sites: number;
  recentDocuments: number;
  siteDocuments: number;
  folderChildren: number;
  authMethods: number;
  registrationCampaign: number;
  conditionalAccessPolicies: number;
  transportRules: number;
}

/**
 * Complete cache settings configuration
 */
export interface CacheSettings {
  maxSize: number;
  ttlSeconds: TtlSeconds;
}

/**
 * Request type for updating cache settings (partial updates supported)
 */
export interface CacheSettingsUpdateRequest {
  maxSize?: number;
  ttlSeconds?: Partial<TtlSeconds>;
}

/**
 * Array of all resource types for iteration
 */
export const RESOURCE_TYPES: readonly ResourceType[] = [
  'teams',
  'sites',
  'recentDocuments',
  'siteDocuments',
  'folderChildren',
  'authMethods',
  'registrationCampaign',
  'conditionalAccessPolicies',
  'transportRules',
] as const;

/**
 * Human-readable labels for each resource type
 */
export const RESOURCE_TYPE_LABELS: Record<ResourceType, string> = {
  teams: 'Teams',
  sites: 'Sites',
  recentDocuments: 'Recent Documents',
  siteDocuments: 'Site Documents',
  folderChildren: 'Folder Contents',
  authMethods: 'Authentication Methods',
  registrationCampaign: 'Registration Campaign',
  conditionalAccessPolicies: 'Conditional Access Policies',
  transportRules: 'Transport Rules',
};

/**
 * Group definition for organizing TTL settings in the UI
 */
export interface TtlSettingsGroup {
  id: string;
  label: string;
  description: string;
  resourceTypes: readonly ResourceType[];
}

/**
 * TTL settings organized by feature area for accordion display
 */
export const TTL_SETTINGS_GROUPS: readonly TtlSettingsGroup[] = [
  {
    id: 'workspace',
    label: 'Workspace',
    description: 'Sidebar navigation and workspace data',
    resourceTypes: ['teams', 'sites'],
  },
  {
    id: 'documents',
    label: 'Documents',
    description: 'Document listings and folder contents',
    resourceTypes: ['recentDocuments', 'siteDocuments', 'folderChildren'],
  },
  {
    id: 'security',
    label: 'Security',
    description: 'Security page data including authentication methods and policies',
    resourceTypes: ['authMethods', 'registrationCampaign', 'conditionalAccessPolicies', 'transportRules'],
  },
] as const;

/**
 * Default cache settings matching backend defaults
 */
export const DEFAULT_CACHE_SETTINGS: CacheSettings = {
  maxSize: 1000,
  ttlSeconds: {
    teams: 600,
    sites: 600,
    recentDocuments: 180,
    siteDocuments: 300,
    folderChildren: 300,
    authMethods: 600,
    registrationCampaign: 600,
    conditionalAccessPolicies: 300,
    transportRules: 300,
  },
};
